import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider, Box, Flex } from '@chakra-ui/react';
import './App.css';
import Sidebar from './Components/SideBar/SideBar';
import LoginPage from './Components/Login/Login';
import ForgotPassword from './Components/PasswordRecovery/ForgotPassword';
import ResetPassword from './Components/PasswordRecovery/ResetPassword';
import MainPage from './Components/MainPage/MainPage';
import CategoriesPage from './Components/Category/CategoryList';
import SubCategoriesPage from './Components/SubCategory/SubCategoryList';
import WorkerList from './Components/Workers/WorkersList';
import { UserProvider, useUser } from './Components/Context/UserContext';
import ProtectedRoute from './Components/Context/ProtectedRoute';
import ManagePrivacyTerms from './Components/TermsConditions/ManagePrivacyTerms';
import ManageUseConditions from './Components/TermsConditions/ManageConditionsUse';
import theme from './Components/Theme/CustomTheme';
import ThemeColorPage from './Components/Theme/AppThemeColors';
import WebUserList from './Components/WebUsers/WebUsersList.js';
import UserList from './Components/Users/UsersList.js';
import WorkerCancellation from './Components/ConversationCancellation/WorkerCancellation';
import UserCancellation from './Components/ConversationCancellation/UserCancellation';
import RejectConfirmations from './Components/RejectConfirmations/RejectConfirmations';
import UserNeedHelp from './Components/NeedHelp/UserNeedHelp.js';
import WorkerNeedHelp from './Components/NeedHelp/WorkerNeedHelp.js';
import FluxoCaixa from './Components/FluxoCaixa/FluxoCaixa.js';
import EmailTest from './Components/Email/EmailTest';
import ProfilePage from './Components/Profile/ProfilePage';
import PreferencesPage from './Components/Profile/PreferencesPage';
import HelpPage from './Components/Help/HelpPage';
import PaymentList from './Components/Payment/PaymentList';
import SubscriptionList from './Components/Payment/SubscriptionList';
import ContasReceber from './Components/FluxoCaixa/ContasReceber';
import ContasPagar from './Components/FluxoCaixa/ContasPagar';

function App() {
  return (
    <ChakraProvider theme={theme}>
      <UserProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/recuperar-senha" element={<ForgotPassword />} />
            <Route path="/redefinir-senha/:token" element={<ResetPassword />} />
            <Route
              path="*"
              element={
                <AuthenticatedLayout>
                  <Routes>
                    <Route path="/main" element={<ProtectedRoute element={MainPage} />} />
                    <Route path="/config/categorias" element={<ProtectedRoute element={CategoriesPage} />} />
                    <Route path="/config/subCategorias" element={<ProtectedRoute element={SubCategoriesPage} />} />
                    <Route path="/config/tema-aplicativo" element={<ProtectedRoute element={ThemeColorPage} />} />
                    <Route path="/contratados/list" element={<ProtectedRoute element={WorkerList} />} />
                    <Route path="/contratantes/list" element={<ProtectedRoute element={UserList} />} />
                    <Route path="/webUsers-list" element={<ProtectedRoute element={WebUserList} />} />
                    <Route path="/financas/fluxocaixa" element={<ProtectedRoute element={FluxoCaixa} />} />
                    <Route path="/emails/teste" element={<ProtectedRoute element={EmailTest} requiredRoles={['Master']} />} />
                    <Route path="/termos/privacidade" element={<ProtectedRoute element={ManagePrivacyTerms} />} />
                    <Route path="/termos/condicoes" element={<ProtectedRoute element={ManageUseConditions} />} />
                    <Route path="/contratados/cancelamento" element={<ProtectedRoute element={WorkerCancellation} />} />
                    <Route path="/contratantes/cancelamento" element={<ProtectedRoute element={UserCancellation} />} />
                    <Route path="/contratantes/recusa-servicos" element={<ProtectedRoute element={RejectConfirmations} />} />
                    <Route path="/contratantes/preciso-ajuda" element={<ProtectedRoute element={UserNeedHelp} />} />
                    <Route path="/contratados/preciso-ajuda" element={<ProtectedRoute element={WorkerNeedHelp} />} />
                    <Route path="/perfil" element={<ProtectedRoute element={ProfilePage} />} />
                    <Route path="/preferencias" element={<ProtectedRoute element={PreferencesPage} />} />
                    <Route path="/ajuda" element={<ProtectedRoute element={HelpPage} />} />
                    <Route path="/financas/pagamentos" element={<ProtectedRoute element={PaymentList} requiredRoles={['Master', 'Administração', 'Financeiro']} />} />
                    <Route path="/financas/assinaturas" element={<ProtectedRoute element={SubscriptionList} requiredRoles={['Master', 'Administração', 'Financeiro']} />} />
                    <Route path="/financas/contasreceber" element={<ProtectedRoute element={ContasReceber} requiredRoles={['Master', 'Administração', 'Financeiro']} />} />
                    <Route path="/financas/contaspagar" element={<ProtectedRoute element={ContasPagar} requiredRoles={['Master', 'Administração', 'Financeiro']} />} />
                    <Route path="/" element={<Navigate replace to="/main" />} />
                  </Routes>
                </AuthenticatedLayout>
              }
            />
          </Routes>
        </Router>
      </UserProvider>
    </ChakraProvider>
  );
}

const AuthenticatedLayout = ({ children }) => {
  const { user } = useUser();

  // Check if the user exists and has a valid token
  if (!user || !user.token) {
    // If no valid user or token, redirect to login
    return <Navigate to="/login" replace />;
  }

  // Redirect to login if token in localStorage doesn't match user token
  const storedToken = localStorage.getItem('userToken_TOVIAPPS');
  if (!storedToken || storedToken !== user.token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Flex minH="100vh" bg="white">
      {/* Sidebar - fixed width */}
      <Sidebar />
      
      {/* Main content area - takes remaining width */}
      <Box 
        flex="1" 
        marginLeft={{ base: 0, lg: "280px" }}
        transition="margin-left 0.3s ease"
        overflowY="auto" /* Enable vertical scrolling */
        width="calc(100% - 280px)"
        maxW="100%"
      >
        <Box 
          bg="white" 
          pl={4} pr={6} py={6} /* Only internal padding */
          borderRadius="0" /* No rounded corners for cleaner look */
          minH="100%" /* At least full height */
          boxShadow="-1px 0 3px rgba(0,0,0,0.05)"
        >
          {children}
        </Box>
      </Box>
    </Flex>
  );
};

export default App;
